import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import Section from '../layout/Section';
import TextReveal from '../animations/TextReveal';
import ScrollReveal from '../animations/ScrollReveal';
import { featureCategories, type FeatureCategory, type FeatureItem } from '../../data/allFeatures';

function FeatureCard({ feature, index }: { feature: FeatureItem; index: number }) {
  const Icon = feature.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      className="group glass rounded-xl p-4 sm:p-5 border border-white/[0.04] hover:border-primary/20 transition-all duration-300"
    >
      <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center mb-3 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h4 className="text-white font-bold text-sm leading-snug mb-1.5">
        {feature.title}
      </h4>
      <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
        {feature.description}
      </p>
    </motion.div>
  );
}

function StatBar({ category }: { category: FeatureCategory }) {
  return (
    <motion.div
      key={category.id + '-stat'}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mb-6 sm:mb-8"
    >
      <div className="flex items-center gap-3 glass rounded-lg px-4 py-2.5 border border-white/[0.06]">
        <div className="text-center">
          <span className="text-red-400/80 text-xs font-medium uppercase tracking-wider">Avant</span>
          <div className="text-white font-bold text-lg line-through decoration-red-400/50">
            {category.stat.before}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-primary flex-shrink-0" />
        <div className="text-center">
          <span className="text-emerald-400/80 text-xs font-medium uppercase tracking-wider">Après</span>
          <div className="text-primary font-black text-lg">
            {category.stat.after}
          </div>
        </div>
      </div>
      <span className="text-gray-500 text-sm font-medium">{category.stat.label}</span>
    </motion.div>
  );
}

export default function AllFeaturesSection() {
  const [activeTab, setActiveTab] = useState(0);
  const tabsRef = useRef<HTMLDivElement>(null);
  const activeCategory = featureCategories[activeTab];

  // Scroll active tab into view on mobile
  useEffect(() => {
    if (!tabsRef.current) return;
    const activeButton = tabsRef.current.children[activeTab] as HTMLElement;
    if (activeButton) {
      activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeTab]);

  return (
    <Section dark id="all-features">
      {/* Title Block */}
      <ScrollReveal>
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold tracking-widest uppercase mb-4 sm:mb-5">
            Toutes les fonctionnalités
          </span>
          <TextReveal
            as="h2"
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight gradient-text"
          >
            91 fonctionnalités. Zéro superflu.
          </TextReveal>
          <p className="mt-4 text-base sm:text-lg max-w-2xl mx-auto text-gray-400 leading-relaxed">
            Découvrez en détail chaque module conçu pour les commerçants africains.
            <br className="hidden sm:block" />
            Cliquez sur une catégorie pour explorer.
          </p>
        </div>
      </ScrollReveal>

      {/* Category Tabs */}
      <ScrollReveal delay={0.1}>
        <div className="relative mb-2">
          {/* Gradient fades for scroll indication */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-dark to-transparent z-10 pointer-events-none sm:hidden" />
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-dark to-transparent z-10 pointer-events-none sm:hidden" />

          <div
            ref={tabsRef}
            className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory sm:flex-wrap sm:justify-center sm:overflow-visible sm:pb-0"
          >
            {featureCategories.map((cat, i) => {
              const Icon = cat.icon;
              const isActive = i === activeTab;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(i)}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap
                    snap-center transition-all duration-300 flex-shrink-0
                    ${isActive
                      ? 'bg-primary text-white shadow-lg shadow-primary/25'
                      : 'glass text-gray-400 hover:text-white hover:border-white/10'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </ScrollReveal>

      {/* Stat Bar */}
      <AnimatePresence mode="wait">
        <StatBar key={activeCategory.id} category={activeCategory} />
      </AnimatePresence>

      {/* Feature Cards Grid */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeCategory.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
        >
          {activeCategory.features.map((feature, i) => (
            <FeatureCard key={feature.title} feature={feature} index={i} />
          ))}
        </motion.div>
      </AnimatePresence>

      {/* Bottom Summary */}
      <ScrollReveal delay={0.2}>
        <div className="mt-8 sm:mt-12 text-center">
          <div className="inline-flex flex-wrap justify-center gap-3 sm:gap-6 text-sm">
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-primary" />
              91 fonctionnalités
            </span>
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-primary" />
              8 modules
            </span>
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-primary" />
              0 jargon technique
            </span>
            <span className="flex items-center gap-1.5 text-gray-500">
              <span className="w-2 h-2 rounded-full bg-primary" />
              100% en français
            </span>
          </div>
        </div>
      </ScrollReveal>
    </Section>
  );
}
